import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Check, ArrowRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { InnovatorsCarousel } from "../components/InnovatorsCarousel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full bg-[#2c6052] border border-[#4a3b31] rounded-md px-4 py-3 text-[#f3ece1] text-sm outline-none focus:border-[#ebc754] placeholder:text-[#8a7968]";

const RequestForm = () => {
  const [f, setF] = useState({ name: "", email: "", mobile: "", business: "", based: "", building: "", website: "", linkedin: "", instagram: "", referrer: "", notes: "", newsletter_consent: false, website_hp: "" });
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim() || !f.mobile.trim() || !f.business.trim() || !f.based.trim() || !f.building.trim()) {
      setErr("Please complete the required fields."); return;
    }
    setState("loading"); setErr("");
    try { await axios.post(`${API}/innovators/request`, f); setState("done"); }
    catch { setState("idle"); setErr("Something went wrong. Please try again."); }
  };
  if (state === "done") return (
    <div data-testid="request-success" className="bg-[#2c6052] border border-[#4a3b31] rounded-xl p-8 text-center">
      <div className="w-14 h-14 rounded-full bg-[#2a3a24] text-[#9fd089] flex items-center justify-center mx-auto mb-4"><Check size={26} /></div>
      <h3 className="feral-serif text-[#f0dbbe] text-3xl mb-3">Thank you.</h3>
      <p className="text-[#c9bcab] text-sm leading-relaxed">You're now on our FERAL Innovators list. We're deliberately keeping each gathering small, so requesting an invitation doesn't guarantee a place. If we think this evening is a good fit, we'll be in touch personally. If not, we'll keep you in mind for future FERAL Innovators gatherings.</p>
    </div>
  );
  return (
    <form onSubmit={submit} data-testid="request-form" className="bg-[#2c6052] border border-[#4a3b31] rounded-xl p-6 md:p-8 space-y-4">
      <input tabIndex={-1} value={f.website_hp} onChange={(e) => set("website_hp", e.target.value)} className="hidden" aria-hidden="true" />
      <div className="grid sm:grid-cols-2 gap-4">
        <input className={field} placeholder="Full name *" value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="req-name" />
        <input className={field} type="email" placeholder="Email *" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="req-email" />
        <input className={field} placeholder="Mobile *" value={f.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="req-mobile" />
        <input className={field} placeholder="Business / organisation / project *" value={f.business} onChange={(e) => set("business", e.target.value)} data-testid="req-business" />
      </div>
      <input className={field} placeholder="Where are you based? *" value={f.based} onChange={(e) => set("based", e.target.value)} data-testid="req-based" />
      <textarea className={`${field} min-h-[90px]`} placeholder="What are you building or working on at the moment? *" value={f.building} onChange={(e) => set("building", e.target.value)} data-testid="req-building" />
      <div className="grid sm:grid-cols-3 gap-4">
        <input className={field} placeholder="Website" value={f.website} onChange={(e) => set("website", e.target.value)} />
        <input className={field} placeholder="LinkedIn" value={f.linkedin} onChange={(e) => set("linkedin", e.target.value)} />
        <input className={field} placeholder="Instagram" value={f.instagram} onChange={(e) => set("instagram", e.target.value)} />
      </div>
      <input className={field} placeholder="Who suggested FERAL Innovators to you?" value={f.referrer} onChange={(e) => set("referrer", e.target.value)} />
      <textarea className={`${field} min-h-[70px]`} placeholder="Anything else you'd like us to know?" value={f.notes} onChange={(e) => set("notes", e.target.value)} />
      <label className="flex items-start gap-3 text-sm text-[#c9bcab] cursor-pointer">
        <input type="checkbox" checked={f.newsletter_consent} onChange={(e) => set("newsletter_consent", e.target.checked)} className="mt-1" data-testid="req-newsletter" />
        <span><strong className="text-[#f0dbbe]">Keep me in the FERAL loop.</strong> Send me occasional news about FERAL Innovators, FERAL Founders, retreats, events and other FERAL happenings.</span>
      </label>
      {err && <p className="text-[#f0a868] text-sm">{err}</p>}
      <button type="submit" disabled={state === "loading"} data-testid="req-submit" className="w-full bg-[#ebc754] hover:bg-[#b9772c] disabled:opacity-60 text-[#1b1512] font-bold tracking-[0.1em] uppercase text-sm py-4 rounded-full transition-colors">
        {state === "loading" ? "Sending…" : "Request an invitation"}
      </button>
    </form>
  );
};

const FeralInnovators = () => {
  return (
    <div className="bg-[#2c6052] min-h-screen text-[#f3ece1]">
      <Navbar tone="sunset" />

      {/* HERO */}
      <section className="relative">
        <div className="relative h-[72vh] min-h-[460px] overflow-hidden">
          <img src="/innov-hero.jpg" alt="Bron Menai Manor at dusk" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#2c6052] via-[#2c6052]/50 to-[#2c6052]/30" />
          <div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-black/80 to-transparent" />
          <div className="absolute inset-0 flex flex-col justify-end px-6 pb-12">
            <div className="max-w-6xl mx-auto w-full">
              <p className="text-[#ebc754] tracking-[0.28em] text-xs font-bold uppercase mb-4" data-testid="public-badge">Invitation only · 40 guests</p>
              <h1 className="feral-serif text-[#ebc754] text-5xl sm:text-7xl lg:text-8xl leading-[0.9] mb-4">FERAL Innovators</h1>
              <p className="text-[#e7d9c6] text-lg sm:text-2xl mb-3 max-w-2xl">For people building interesting things in North Wales.</p>
              <p className="text-[#c9bcab] tracking-[0.1em] text-sm uppercase">Tuesday 10 November · Bron Menai Manor, Anglesey</p>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="px-6 py-16 md:py-24">
        <div className="max-w-2xl mx-auto space-y-5 text-[#d8cabb] text-base sm:text-lg leading-relaxed">
          <p>We're bringing together a small group of founders, business owners, creatives, investors and people doing interesting things across Anglesey and North Wales.</p>
          <p className="text-[#f0dbbe] font-semibold">The idea is simple.</p>
          <p data-testid="innovators-aim">Our aim is to bring the people shaping what comes next in Ynys Môn and North Wales into one room, sparking the ideas, connections and collaborations that help ourselves and the region reach its full potential.</p>
          <p>Put brilliant people in a beautiful house, feed everyone properly, create some interesting conversations and see what comes from it.</p>
          <p className="feral-serif text-[#ebc754] text-2xl md:text-3xl">This isn't traditional networking.</p>
          <p>FERAL Innovators is deliberately small and invitations are offered individually.</p>
          <p>The evening will include food from Grace &amp; James, drinks from BAA BAA Bar and an informal FERAL Conversation with Robin from Dylan's.</p>
          <p className="text-[#f0dbbe]">Places are limited to 40.</p>
        </div>
      </section>

      <InnovatorsCarousel />

      {/* REQUEST AN INVITATION */}
      <section id="request" className="px-6 py-16 md:py-24">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="feral-serif text-[#f0dbbe] text-4xl md:text-5xl mb-4">Request an invitation</h2>
            <p className="text-[#c9bcab] text-sm leading-relaxed max-w-xl mx-auto">FERAL Innovators is invitation only. If you'd like to join us, tell us a little about yourself below. Each gathering is deliberately kept small, so submitting this form doesn't guarantee a place. We'll also use this list when putting together future FERAL Innovators evenings.</p>
          </div>
          <RequestForm />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeralInnovators;
